







import {
  Component,
  Prop,
  Vue
} from "@/shared/vuepack";
import {
  BankDailyBalanceRecord,
  HsaDailyBalance
} from "@/shared/types";
import { GChart } from "vue-google-charts";
import moment from "moment";
import _ from "lodash";
import { Moment } from "moment/moment";
import { useThemeStore } from "@/shared/store/theme";
import { formatCurrency } from "@/shared/currency.js";

@Component({
  components: {
    GChart,
  }
})
export default class AccountBalanceStackedChart extends Vue {
  themeStore = useThemeStore();

  @Prop()
  height: number;

  @Prop()
  classes: string;

  @Prop({default: "Cash"})
  cashBalanceLabel: string;

  @Prop({default:  () => []})
  cashBalanceDataSet: HsaDailyBalance[];

  @Prop({default: () => []})
  investmentBalanceDataSet: BankDailyBalanceRecord[];


  get getChartOptions () {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor
      },
      titlePosition: "none",
      colors: [this.themeStore.secondaryColor, this.themeStore.tertiaryColor],
      tooltip: {
        isHtml: true,
      },
      focusTarget: "category", // tooltip is active for all series
      chartArea: {
        height: "78%",
      },
      hAxis: {
        format: "M/dd/yy",
        gridlines: {
          color: "transparent"
        }
      },
      vAxis: {
        minValue: 1,
        baseline: 0,
        baselineColor: "black",
        format: "$###,###",
        viewWindow: {
          min: 1 // hide the baseline and the $0 y axis label
        },
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        }
      },
      height: this.height,
      legend: { position: "top" },
      isStacked: true,
    };
  }

  get getChartData () {
    return this.mergeCashAndInvestmentDataSets();
  }

  mergeCashAndInvestmentDataSets () {
    // chart headers are the first item in the array
    const headers: any[] = ["Date"];
    headers.push({ role: "tooltip", type: "string", p: { html: true } });

    if (this.cashBalanceDataSet.length > 0) {
      headers.push(this.cashBalanceLabel);
    }
    if (this.investmentBalanceDataSet.length > 0) {
      headers.push("Investments");
    }
    const dataPoints = [];

    // sort both lists by date
    let cashBalances: HsaDailyBalance[] = _.orderBy(this.cashBalanceDataSet, x => x.timestamp);
    const investmentBalances: BankDailyBalanceRecord[] = _.orderBy(this.investmentBalanceDataSet, x => x.timestamp);

    // find min date and max date
    const dateRange = this.calculateMinAndMaxDatesForData(cashBalances, investmentBalances);

    // if cash balance doesn't have a datapoint at the start of the range, we can make one
    // by reducing the balance by the amounts of that day's transactions.
    // The graph will be messed up otherwise
    if (cashBalances.length > 0 && dateRange.minDate.isBefore(cashBalances[0].date) && cashBalances[0].transactionSummaries) {
      const daySum = cashBalances[0].transactionSummaries.reduce((total, tx) => total - tx.amount, cashBalances[0].balance);
      cashBalances = [
        {
          date: dateRange.minDate.format("YYYY-MM-DD"),
          balance: daySum
        },
        ...cashBalances
      ];
    }

    // create a data point for each day between min and max dates
    let i = dateRange.minDate;
    while (i.isSameOrBefore(dateRange.maxDate)) {
      let cashValue = null;
      let invValue = null;

      //  find cash balance at this date
      if (cashBalances.length > 0) {
        this.checkAndRemoveFirstItem(i, cashBalances);
        if (i.isSameOrAfter(cashBalances[0].date)) {
          cashValue = cashBalances[0].balance;
        }
      }

      //  find inv balance at this date
      if (investmentBalances.length > 0) {
        this.checkAndRemoveFirstItem(i, investmentBalances);
        if (i.isSameOrAfter(investmentBalances[0].date)) {
          invValue = investmentBalances[0].balance;
        }
      }

      // create data point and add to results
      const dataForPoint = {
        date: i.toDate(),
        cash: cashValue,
        investment: invValue,
      };
      const dataPoint: any[] = [dataForPoint.date];
      dataPoint.push(this.tooltipFormatter(dataForPoint));

      if (this.cashBalanceDataSet.length > 0) {
        dataPoint.push(dataForPoint.cash);
      }
      if (this.investmentBalanceDataSet.length > 0) {
        dataPoint.push(dataForPoint.investment);
      }

      dataPoints.push(dataPoint);

      i = i.add(1, "day");
    }

    return [
      headers,
      ...dataPoints
    ];
  }

  checkAndRemoveFirstItem (date: Moment, lst: BankDailyBalanceRecord[] | HsaDailyBalance[]) {
    if (lst.length > 1) {
      if (date.isSameOrAfter(lst[1].date)) {
        lst.shift();
      }
    }
  }

  calculateMinAndMaxDatesForData (lst1, lst2) {
    let minDate: Moment;
    let maxDate: Moment;

    if (lst1.length > 0) {
      minDate = moment(lst1[0].date);
      maxDate = moment(lst1[lst1.length - 1].date);
    } else {
      minDate = moment();
      maxDate = moment();

    }
    if (lst2.length > 0) {
      const investMinDate = moment(lst2[0].date);
      const investMaxDate = moment(lst2[lst2.length - 1].date);
      if (minDate.isAfter(investMinDate)) {
        minDate = investMinDate;
      }
      if (maxDate.isBefore(investMaxDate)) {
        maxDate = investMaxDate;
      }
    }
    if (minDate.isSame(maxDate)) {
      maxDate = maxDate.add(1, "d");
    }
    return {
      minDate,
      maxDate
    };
  }

  tooltipFormatter (data: { date: Date, cash: number, investment: number }): string {

    const cashMarkup = `<div margin-bottom: 3px;">
                          ${this.cashBalanceLabel}:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${formatCurrency(data.cash)}
                        </div>`;
    const investmentMarkup = `<div margin-bottom: 3px;">
                                Investment:
                              </div>
                              <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                                ${formatCurrency(data.investment)}
                              </div>`;

    return `<div style="padding: 2px 10px; min-width: 120px;">
              <div style="font-weight: bold; margin-bottom: 3px;">
                ${moment(data.date).format("MMM DD")}
              </div>
              ${data.cash !== null ? cashMarkup : ""}
              ${data.investment !== null ? investmentMarkup : ""}
            </div>`;
  }
}
